import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight, GripVertical, ImagePlus, Loader2, Pencil, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { resolveSrc } from "@/components/Gallery";

const API = process.env.REACT_APP_BACKEND_URL;
const COLLECTIONS = [
  { id: "gallery", label: "Galleria", hint: "Sezione \"Sul palco e sul mare\"" },
  { id: "studio", label: "In studio", hint: "Pellicola backstage registrazioni" },
];

function UploadBox({ headers, collection, onUploaded }) {
  const [files, setFiles] = useState([]);
  const [alt, setAlt] = useState("");
  const [progress, setProgress] = useState(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const pick = (list) => {
    const imgs = Array.from(list || []).filter((f) => f.type.startsWith("image/"));
    if (imgs.length === 0) toast("Seleziona file immagine (JPG, PNG, WEBP, HEIC)");
    setFiles(imgs);
  };

  const upload = async () => {
    if (files.length === 0) return;
    let done = 0;
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("alt", alt);
      fd.append("collection", collection);
      try {
        setProgress({ i: done + 1, n: files.length, pct: 0 });
        const { data } = await axios.post(`${API}/api/admin/gallery`, fd, {
          headers: headers(),
          onUploadProgress: (e) =>
            setProgress({ i: done + 1, n: files.length, pct: e.total ? Math.round((e.loaded / e.total) * 100) : 50 }),
        });
        onUploaded(data);
        done += 1;
      } catch (err) {
        toast(err.response?.data?.detail || `Caricamento di ${file.name} non riuscito`);
      }
    }
    setProgress(null);
    setFiles([]);
    setAlt("");
    if (inputRef.current) inputRef.current.value = "";
    if (done > 0) toast(done === 1 ? "Foto caricata" : `${done} foto caricate`);
  };

  return (
    <div
      data-testid="gallery-upload-box"
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes("Files")) {
          e.preventDefault();
          setDragging(true);
        }
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        if (!e.dataTransfer.types.includes("Files")) return;
        e.preventDefault();
        setDragging(false);
        pick(e.dataTransfer.files);
      }}
      className={`border border-dashed p-6 transition-colors duration-200 ${dragging ? "border-red-600 bg-red-950/10" : "border-neutral-700 bg-[#0D0D0D]"}`}
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <button
          type="button"
          data-testid="gallery-pick-button"
          onClick={() => inputRef.current?.click()}
          className="inline-flex items-center gap-2 border border-neutral-700 px-4 py-2.5 font-mono-tac text-xs uppercase tracking-[0.2em] text-neutral-200 hover:border-red-600 hover:text-red-500"
        >
          <ImagePlus className="h-4 w-4" /> Scegli foto
        </button>
        <input ref={inputRef} data-testid="gallery-file-input" type="file" accept="image/*" multiple onChange={(e) => pick(e.target.files)} className="hidden" />
        <p className="text-sm text-neutral-500">
          {files.length > 0 ? `${files.length} file selezionati` : "…oppure trascina qui le immagini. Max 10 MB l'una, vengono ottimizzate in automatico."}
        </p>
      </div>
      {files.length > 0 && (
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <input
            data-testid="gallery-alt-input"
            value={alt}
            onChange={(e) => setAlt(e.target.value)}
            placeholder="Didascalia (opzionale, vale per tutte)"
            className="h-11 flex-1 border border-neutral-800 bg-black/40 px-3 text-sm text-neutral-100 focus:border-red-700 focus:outline-none"
          />
          <button
            type="button"
            data-testid="gallery-upload-button"
            onClick={upload}
            disabled={progress !== null}
            className="inline-flex h-11 items-center justify-center gap-2 bg-[#C8102E] px-5 font-mono-tac text-xs uppercase tracking-[0.2em] text-white hover:bg-[#FF2A4B] disabled:opacity-60"
          >
            {progress ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {progress ? `${progress.i}/${progress.n} · ${progress.pct}%` : "Carica"}
          </button>
        </div>
      )}
    </div>
  );
}

export default function GalleryAdmin({ headers, onUnauthorized }) {
  const [collection, setCollection] = useState("gallery");
  const [photos, setPhotos] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setPhotos(null);
    try {
      const { data } = await axios.get(`${API}/api/gallery`, { params: { collection } });
      setPhotos(data);
    } catch {
      toast("Impossibile caricare le foto");
    }
  }, [collection]);
  useEffect(() => {
    load();
  }, [load]);

  const guard = (err) => {
    if (err.response?.status === 401) onUnauthorized();
    else toast(err.response?.data?.detail || "Operazione non riuscita");
  };

  const persistOrder = async (list) => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/gallery/reorder`, { collection, ids: list.map((p) => p.id) }, { headers: headers() });
      toast("Ordine salvato");
    } catch (err) {
      guard(err);
      load();
    } finally {
      setSaving(false);
    }
  };

  const move = (fromIdx, toIdx) => {
    if (fromIdx === toIdx || toIdx < 0 || toIdx >= photos.length) return;
    const list = [...photos];
    const [item] = list.splice(fromIdx, 1);
    list.splice(toIdx, 0, item);
    setPhotos(list);
    persistOrder(list);
  };

  const onDrop = (targetId) => {
    if (!dragId || dragId === targetId) return;
    const from = photos.findIndex((p) => p.id === dragId);
    const to = photos.findIndex((p) => p.id === targetId);
    move(from, to);
    setDragId(null);
    setOverId(null);
  };

  const editAlt = async (p) => {
    const alt = window.prompt("Didascalia della foto", p.alt);
    if (alt === null || alt.trim() === p.alt) return;
    try {
      const { data } = await axios.patch(`${API}/api/admin/gallery/${p.id}`, { alt: alt.trim() }, { headers: headers() });
      setPhotos((list) => list.map((x) => (x.id === p.id ? data : x)));
      toast("Didascalia aggiornata");
    } catch (err) {
      guard(err);
    }
  };

  const toggleWide = async (p) => {
    try {
      const { data } = await axios.patch(`${API}/api/admin/gallery/${p.id}`, { wide: !p.wide }, { headers: headers() });
      setPhotos((list) => list.map((x) => (x.id === p.id ? data : x)));
    } catch (err) {
      guard(err);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Rimuovere questa foto?\n${p.alt}`)) return;
    try {
      await axios.delete(`${API}/api/admin/gallery/${p.id}`, { headers: headers() });
      setPhotos((list) => list.filter((x) => x.id !== p.id));
      toast("Foto rimossa");
    } catch (err) {
      guard(err);
    }
  };

  const current = COLLECTIONS.find((c) => c.id === collection);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2" role="tablist" aria-label="Raccolta">
        {COLLECTIONS.map((c) => (
          <button
            key={c.id}
            role="tab"
            aria-selected={collection === c.id}
            data-testid={`gallery-collection-${c.id}`}
            onClick={() => setCollection(c.id)}
            className={`border px-4 py-2 font-mono-tac text-xs uppercase tracking-[0.2em] transition-colors duration-200 ${
              collection === c.id ? "border-[#C8102E] bg-red-950/20 text-neutral-100" : "border-neutral-800 text-neutral-500 hover:text-red-500"
            }`}
          >
            {c.label}
          </button>
        ))}
        <span className="ml-2 text-xs text-neutral-500">{current.hint}</span>
      </div>

      <div className="mt-5">
        <UploadBox headers={headers} collection={collection} onUploaded={(p) => setPhotos((list) => [...(list || []), p])} />
      </div>

      <div className="mt-6 flex items-center justify-between">
        <p className="font-mono-tac text-[10px] uppercase tracking-[0.3em] text-neutral-500">
          {photos ? `${photos.length} foto · trascina per riordinare` : "Caricamento…"}
        </p>
        {saving && <span className="font-mono-tac text-[10px] uppercase tracking-[0.2em] text-red-500">Salvo l'ordine…</span>}
      </div>

      <div data-testid="admin-gallery-grid" className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {photos?.map((p, idx) => (
          <figure
            key={p.id}
            data-testid="admin-gallery-item"
            data-photo-id={p.id}
            draggable
            onDragStart={(e) => {
              e.dataTransfer.effectAllowed = "move";
              e.dataTransfer.setData("text/plain", p.id);
              setDragId(p.id);
            }}
            onDragEnd={() => {
              setDragId(null);
              setOverId(null);
            }}
            onDragOver={(e) => {
              if (!dragId) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = "move";
              if (overId !== p.id) setOverId(p.id);
            }}
            onDrop={(e) => {
              e.preventDefault();
              onDrop(p.id);
            }}
            className={`group relative cursor-grab overflow-hidden border bg-[#0D0D0D] transition-all duration-200 active:cursor-grabbing ${
              overId === p.id && dragId !== p.id ? "border-[#C8102E] shadow-[0_0_20px_rgba(200,16,46,0.4)]" : "border-neutral-800"
            } ${dragId === p.id ? "opacity-40" : ""}`}
          >
            <img src={resolveSrc(p.src)} alt={p.alt} loading="lazy" draggable={false} className="aspect-square w-full object-cover" />
            <span className="absolute left-2 top-2 flex items-center gap-1 border border-neutral-700 bg-black/70 px-1.5 py-0.5 font-mono-tac text-[9px] tracking-[0.2em] text-neutral-300">
              <GripVertical className="h-3 w-3" />
              {String(idx + 1).padStart(2, "0")}
            </span>
            {p.uploaded && (
              <span className="absolute right-2 top-2 border border-red-900/60 bg-black/70 px-1.5 py-0.5 font-mono-tac text-[8px] uppercase tracking-[0.2em] text-red-500 group-hover:opacity-0">caricata</span>
            )}
            <figcaption className="flex items-start justify-between gap-2 p-3">
              <span className="min-w-0 flex-1 truncate text-xs text-neutral-300" title={p.alt}>{p.alt}</span>
              <button data-testid="admin-gallery-wide" onClick={() => toggleWide(p)} title={p.wide ? "Orizzontale" : "Verticale"} className="font-mono-tac text-[9px] uppercase tracking-[0.2em] text-neutral-500 hover:text-red-500">
                {p.wide ? "16:10" : "3:4"}
              </button>
            </figcaption>
            <div className="absolute right-2 top-2 flex gap-1 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
              <button data-testid="admin-gallery-move-left" onClick={() => move(idx, idx - 1)} disabled={idx === 0} aria-label="Sposta prima" className="flex h-8 w-8 items-center justify-center border border-neutral-700 bg-black/70 text-neutral-200 hover:border-red-600 hover:text-red-500 disabled:opacity-30">
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>
              <button data-testid="admin-gallery-move-right" onClick={() => move(idx, idx + 1)} disabled={idx === photos.length - 1} aria-label="Sposta dopo" className="flex h-8 w-8 items-center justify-center border border-neutral-700 bg-black/70 text-neutral-200 hover:border-red-600 hover:text-red-500 disabled:opacity-30">
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
              <button data-testid="admin-gallery-edit" onClick={() => editAlt(p)} aria-label="Modifica didascalia" className="flex h-8 w-8 items-center justify-center border border-neutral-700 bg-black/70 text-neutral-200 hover:border-red-600 hover:text-red-500">
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button data-testid="admin-gallery-delete" onClick={() => remove(p)} aria-label="Rimuovi foto" className="flex h-8 w-8 items-center justify-center border border-neutral-700 bg-black/70 text-neutral-200 hover:border-red-600 hover:text-red-500">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </figure>
        ))}
      </div>
    </div>
  );
}
